import { useState } from 'react';
import { Player } from '../types';

export const useUndoGame = (
  players: Player[],
  setPlayers: React.Dispatch<React.SetStateAction<Player[]>>,
  currentPlayerIndex: number,
  setCurrentPlayerIndex: React.Dispatch<React.SetStateAction<number>>,
  saveGameStateToLocalStorage: () => void
) => {
  const [showUndoModal, setShowUndoModal] = useState(false);

  const handleUndoClick = () => {
    setShowUndoModal(true);
  };

  const confirmUndo = () => {
    setShowUndoModal(false);
    undoLastTurn();
  };

  const cancelUndo = () => {
    setShowUndoModal(false);
  };

  const undoLastTurn = () => {
    setPlayers((prevPlayers) => {
      const updatedPlayers = [...prevPlayers];

      // If there's only one player, handle undo for the same player
      if (updatedPlayers.length === 1) {
        const currentPlayer = updatedPlayers[0];

        // If there is no history to undo, return early
        if (currentPlayer.history.length === 0) {
          return prevPlayers;
        }

        // Get the last history entry and remove it
        const lastHistoryEntry = currentPlayer.history.pop();

        // Calculate score delta. Final-throw attempts do not change the player's score.
        const lastTurnScore = lastHistoryEntry ? (lastHistoryEntry.isFinalThrowAttempt ? 0 : lastHistoryEntry.turnScore) : 0;
        const updatedScore = currentPlayer.score - lastTurnScore;

        // Update the current player's score, reset dice, and recompute final-throw state
        updatedPlayers[0] = {
          ...currentPlayer,
          score: Math.max(updatedScore, 0), // Ensure score doesn't go below 0
          history: currentPlayer.history,
          dice: currentPlayer.dice.map((die) => ({ ...die, value: 0, isFrozen: false })), // Reset dice
          isCurrent: true, // Keep the current player active
          isFinalThrow: Math.max(updatedScore, 0) >= 90 && !currentPlayer.hasWon,
        };

        return updatedPlayers;
      }

      // Multi-player case: Get the previous player's index
      const previousPlayerIndex = (currentPlayerIndex - 1 + updatedPlayers.length) % updatedPlayers.length;
      const previousPlayer = updatedPlayers[previousPlayerIndex];
      const currentPlayer = updatedPlayers[currentPlayerIndex];

      // If there is no history to undo for the previous player, return early
      if (previousPlayer.history.length === 0) {
        return prevPlayers;
      }

      // Get the last history entry and remove it
      const lastHistoryEntry = previousPlayer.history.pop();

      // Calculate score delta. Final-throw attempts do not change the player's score.
      const lastTurnScore = lastHistoryEntry ? (lastHistoryEntry.isFinalThrowAttempt ? 0 : lastHistoryEntry.turnScore) : 0;
      const updatedScore = previousPlayer.score - lastTurnScore;

      // Update the previous player's score, reset dice, and recompute final-throw state
      updatedPlayers[previousPlayerIndex] = {
        ...previousPlayer,
        score: Math.max(updatedScore, 0), // Ensure score doesn't go below 0
        history: previousPlayer.history,
        dice: previousPlayer.dice.map((die) => ({ ...die, value: 0, isFrozen: false })), // Reset dice
        isCurrent: true, // Make the previous player active
        isFinalThrow: Math.max(updatedScore, 0) >= 90 && !previousPlayer.hasWon,
      };

      // Set the current player (who's card is open) back to inactive (minimized)
      updatedPlayers[currentPlayerIndex] = {
        ...currentPlayer,
        isCurrent: false, // Minimize the current player's card
      };

      // Set the current player back to the previous player
      setCurrentPlayerIndex(previousPlayerIndex);

      return updatedPlayers;
    });
    saveGameStateToLocalStorage();
  };

  const canUndo = players.every(player => player.history.length === 0);

  return {
    showUndoModal,
    handleUndoClick,
    confirmUndo,
    cancelUndo,
    canUndo,
  };
};